import { useState, useRef, useMemo } from 'react';
import {
  LayoutDashboard, Plus, Pencil, Trash2, Search, Briefcase,
  FileText, Calendar, CheckCircle, XCircle
} from 'lucide-react';
import {
  Button, MessagePlugin, Dialog, Form, Input, Switch, Table, Space,
  Popconfirm, Select
} from 'tdesign-react';
import ProductRichTextEditor from '../../components/ProductRichTextEditor';

interface Position {
  id: number;
  code: string;
  name: string;
  employmentType: string;
  responsibility: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

const employmentTypeOptions = [
  { label: '全职', value: 'full-time', bg: '#e8f4ff', color: '#1c5bff' },
  { label: '实习', value: 'intern', bg: '#e8f9ee', color: '#059465' },
  { label: '兼职', value: 'part-time', bg: '#fff3e6', color: '#ed7b2f' },
  { label: '外包', value: 'outsourcing', bg: '#f3e8ff', color: '#722ed1' },
  { label: '校招', value: 'campus', bg: '#e6f7ff', color: '#08979c' },
];

const getEmploymentLabel = (value: string) => {
  return employmentTypeOptions.find(o => o.value === value)?.label || value;
};

const getEmploymentStyle = (value: string) => {
  const option = employmentTypeOptions.find(o => o.value === value);
  return option ? { backgroundColor: option.bg, color: option.color } : { backgroundColor: '#f5f5f5', color: '#666' };
};

const initialPositions: Position[] = [
  {
    id: 1,
    code: 'POS001',
    name: '前端高级工程师',
    employmentType: 'full-time',
    responsibility: '<p>1. 负责公司核心产品的前端架构设计与开发；</p><p>2. 优化前端性能，提升用户体验；</p><p>3. 参与技术方案评审，指导初级工程师成长；</p><p>4. 跟进前端技术栈发展，推动技术创新。</p>',
    status: 'active',
    createdAt: '2024-01-15'
  },
  {
    id: 2,
    code: 'POS002',
    name: 'UI/UX 设计师',
    employmentType: 'full-time',
    responsibility: '<p>1. 负责产品界面视觉设计；</p><p>2. 参与用户体验优化；</p><p>3. 与产品经理协作完成设计方案；</p><p>4. 维护设计规范和组件库。</p>',
    status: 'active',
    createdAt: '2024-02-20'
  },
  {
    id: 3,
    code: 'POS003',
    name: '后端开发实习生',
    employmentType: 'intern',
    responsibility: '<p>1. 协助高级工程师完成接口开发；</p><p>2. 参与代码审查和单元测试；</p><p>3. 学习并掌握公司技术栈；</p><p>4. 每周提交实习报告。</p>',
    status: 'active',
    createdAt: '2024-03-01'
  },
  {
    id: 4,
    code: 'POS004',
    name: '市场推广兼职',
    employmentType: 'part-time',
    responsibility: '<p>1. 协助市场部完成推广活动；</p><p>2. 参与社交媒体运营；</p><p>3. 撰写营销文案；</p><p>4. 每周工作不少于 3 天。</p>',
    status: 'inactive',
    createdAt: '2024-03-10'
  },
  {
    id: 5,
    code: 'POS005',
    name: '数据分析工程师',
    employmentType: 'full-time',
    responsibility: '<p>1. 搭建和维护数据分析体系；</p><p>2. 编写数据处理脚本；</p><p>3. 输出数据分析报告；</p><p>4. 为业务决策提供数据支持。</p>',
    status: 'active',
    createdAt: '2024-04-01'
  },
  {
    id: 6,
    code: 'POS006',
    name: '客服外包人员',
    employmentType: 'outsourcing',
    responsibility: '<p>1. 负责客户咨询响应；</p><p>2. 处理订单相关问题；</p><p>3. 记录客户反馈并上报；</p><p>4. 按时完成服务指标。</p>',
    status: 'active',
    createdAt: '2024-04-15'
  },
  {
    id: 7,
    code: 'POS007',
    name: '校园招聘专员',
    employmentType: 'campus',
    responsibility: '<p>1. 负责校园招聘全流程；</p><p>2. 维护高校合作关系；</p><p>3. 组织校园宣讲会；</p><p>4. 跟进候选人面试流程。</p>',
    status: 'active',
    createdAt: '2024-05-01'
  }
];

const emptyForm: Omit<Position, 'id' | 'createdAt'> = {
  code: '',
  name: '',
  employmentType: '',
  responsibility: '',
  status: 'active'
};

export default function PositionManage() {
  const [positions, setPositions] = useState<Position[]>(initialPositions);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [currentForm, setCurrentForm] = useState(emptyForm);
  const formRef = useRef<any>(null);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const filteredPositions = useMemo(() => {
    let list = [...positions];
    if (searchText.trim()) {
      const kw = searchText.trim().toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(kw) ||
        p.code.toLowerCase().includes(kw)
      );
    }
    if (typeFilter) list = list.filter(p => p.employmentType === typeFilter);
    if (statusFilter) list = list.filter(p => p.status === statusFilter);
    return list;
  }, [positions, searchText, typeFilter, statusFilter]);

  const pagedData = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    return filteredPositions.slice(start, start + pagination.pageSize);
  }, [filteredPositions, pagination]);

  const handleSearch = () => setPagination({ ...pagination, current: 1 });

  const handleReset = () => {
    setSearchText('');
    setTypeFilter('');
    setStatusFilter('');
    setPagination({ ...pagination, current: 1 });
  };

  const openCreate = () => {
    setEditingPosition(null);
    setCurrentForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (pos: Position) => {
    setEditingPosition(pos);
    setCurrentForm({
      code: pos.code,
      name: pos.name,
      employmentType: pos.employmentType,
      responsibility: pos.responsibility,
      status: pos.status
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!currentForm.code.trim()) {
      MessagePlugin.warning('请输入职位编号');
      return;
    }
    if (!currentForm.name.trim()) {
      MessagePlugin.warning('请输入职位名称');
      return;
    }
    if (!currentForm.employmentType) {
      MessagePlugin.warning('请选择用工类型');
      return;
    }

    if (editingPosition) {
      setPositions(pos => pos.map(p => p.id === editingPosition.id ? { ...p, ...currentForm } : p));
      MessagePlugin.success('更新成功');
    } else {
      const maxId = Math.max(...positions.map(p => p.id), 0);
      const newPos: Position = {
        ...currentForm,
        id: maxId + 1,
        createdAt: new Date().toISOString().split('T')[0]
      };
      setPositions([...positions, newPos]);
      MessagePlugin.success('新增成功');
    }
    setShowModal(false);
    setEditingPosition(null);
  };

  const handleDelete = (id: number) => {
    setPositions(pos => pos.filter(p => p.id !== id));
    MessagePlugin.success('删除成功');
  };

  const toggleStatus = (id: number) => {
    setPositions(pos => pos.map(p => p.id === id ? {
      ...p,
      status: p.status === 'active' ? 'inactive' : 'active'
    } : p));
    MessagePlugin.success('状态已更新');
  };

  const columns = [
    {
      colKey: 'code',
      title: '职位编号',
      width: 120,
      cell: ({ row }: { row: Position }) => (
        <div className="flex items-center gap-1.5 text-sm text-gray-700">
          {row.code}
        </div>
      )
    },
    {
      colKey: 'name',
      title: '职位名称',
      width: 160,
      cell: ({ row }: { row: Position }) => (
        <span className="text-sm font-medium text-gray-900">{row.name}</span>
      )
    },
    {
      colKey: 'employmentType',
      title: '用工类型',
      width: 120,
      cell: ({ row }: { row: Position }) => (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border"
          style={{
            backgroundColor: getEmploymentStyle(row.employmentType).backgroundColor,
            color: getEmploymentStyle(row.employmentType).color,
            borderColor: getEmploymentStyle(row.employmentType).color + '30'
          }}
        >
          {getEmploymentLabel(row.employmentType)}
        </span>
      )
    },
    {
      colKey: 'responsibility',
      title: '工作职责',
      width: 200,
      cell: ({ row }: { row: Position }) => {
        const text = row.responsibility.replace(/<[^>]*>/g, '').trim();
        const preview = text.length > 50 ? text.slice(0, 50) + '...' : text;
        return (
          <div className="flex items-center gap-1 text-sm text-gray-600 truncate max-w-[180px]" title={text}>
            <span className="truncate">{preview || '-'}</span>
          </div>
        );
      }
    },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: { row: Position }) => (
        <Switch
          value={row.status === 'active'}
          onChange={() => toggleStatus(row.id)}
        />
      )
    },
    {
      colKey: 'createdAt',
      title: '创建时间',
      width: 120,
      cell: ({ row }: { row: Position }) => (
        <div className="flex items-center gap-1 text-sm text-gray-600">
          {row.createdAt}
        </div>
      )
    },
    {
      colKey: 'action',
      title: '操作',
      width: 180,
      fixed: 'right',
      cell: ({ row }: { row: Position }) => (
        <Space>
          <Button
            theme="primary"
            variant="text"
            size="small"
            icon={<Pencil className="w-4 h-4" />}
            onClick={() => openEdit(row)}
          >
            编辑
          </Button>
          <Popconfirm content="确定删除该职位吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" size="small" icon={<Trash2 className="w-4 h-4" />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <LayoutDashboard className="w-6 h-6 text-[#1c5bff]" />
          职位管理
        </h1>
        <Button theme="primary" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
          新增职位
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-gray-600 mb-1">搜索</label>
            <Input
              value={searchText}
              onChange={setSearchText}
              placeholder="职位编号/名称"
              clearable
              onClick={handleSearch}
            />
          </div>
          <div className="w-44">
            <label className="block text-sm text-gray-600 mb-1">用工类型</label>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              placeholder="全部"
              clearable
            >
              <Select.Option value="">全部</Select.Option>
              {employmentTypeOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </div>
          <div className="w-44">
            <label className="block text-sm text-gray-600 mb-1">状态</label>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="全部"
              clearable
            >
              <Select.Option value="">全部</Select.Option>
              <Select.Option value="active">启用</Select.Option>
              <Select.Option value="inactive">停用</Select.Option>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button theme="primary" icon={<Search className="w-4 h-4" />} onClick={handleSearch}>
              搜索
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table
          columns={columns as any}
          data={pagedData}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredPositions.length,
            showJumper: true,
            showPageSize: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (pageInfo: any) => {
              setPagination(prev => ({
                ...prev,
                current: pageInfo.current ?? prev.current,
                pageSize: pageInfo.pageSize ?? prev.pageSize
              }));
            }
          }}
          bordered={false}
          hover
          table-layout="fixed"
        />
      </div>

      <Dialog
        header={editingPosition ? '编辑职位' : '新增职位'}
        visible={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPosition(null);
        }}
        onConfirm={handleSave}
        confirmBtn="保存"
        width={700}
      >
        <Form ref={formRef} initialData={editingPosition || {}} labelWidth={100}>
          <div className="space-y-4 overflow-y-auto max-h-[70vh]">
            <Form.FormItem label="职位编号" name="code" rules={[{ required: true, message: '请输入职位编号' }]}>
                <Input
                  value={currentForm.code}
                  onChange={(val) => setCurrentForm({ ...currentForm, code: val })}
                  placeholder="如 POS001"
                />
            </Form.FormItem>

            <Form.FormItem label="职位名称" name="name" rules={[{ required: true, message: '请输入职位名称' }]}>
                <Input
                    value={currentForm.name}
                    onChange={(val) => setCurrentForm({ ...currentForm, name: val })}
                    placeholder="请输入职位名称"
                />
            </Form.FormItem>

            <Form.FormItem label="用工类型" name="employmentType" rules={[{ required: true, message: '请选择用工类型' }]}>
              <Select
                value={currentForm.employmentType}
                onChange={(val) => setCurrentForm({ ...currentForm, employmentType: val as string })}
                placeholder="请选择用工类型"
                clearable
              >
                {employmentTypeOptions.map(o => (
                  <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                ))}
              </Select>
            </Form.FormItem>

            <Form.FormItem label="工作职责" name="responsibility" rules={[{ required: true, message: '请填写工作职责' }]}>
              <ProductRichTextEditor
                value={currentForm.responsibility}
                onChange={(val) => setCurrentForm({ ...currentForm, responsibility: val })}
                showHeight={false}
              />
            </Form.FormItem>

            <Form.FormItem label="状态" name="status">
              <Select
                value={currentForm.status}
                onChange={(val) => setCurrentForm({ ...currentForm, status: val as "active" | "inactive" })}
              >
                <Select.Option value="active">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    启用
                  </span>
                </Select.Option>
                <Select.Option value="inactive">
                  <span className="flex items-center gap-1.5">
                    <XCircle className="w-4 h-4 text-gray-400" />
                    停用
                  </span>
                </Select.Option>
              </Select>
            </Form.FormItem>
          </div>
        </Form>
      </Dialog>
    </div>
  );
}
